import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowRight, Printer, Loader2, CheckCircle2, Circle, Target, TrendingUp } from "lucide-react";
import api from "@/lib/api";

const PRINT_CSS = `
@media print {
  .no-print { display: none !important; }
  body { background: #ffffff !important; }
  .report-page { box-shadow: none !important; margin: 0 !important; }
  @page { margin: 14mm; }
}
`;

const Bar = ({ pct, color = "#734F96" }) => (
  <div className="h-2.5 w-full rounded-full bg-slate-200 overflow-hidden">
    <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
  </div>
);

const STATUS_AR = { on_track: "على المسار", ahead: "متقدّم عن الهدف", behind: "متأخر عن الهدف", mixed: "أداء متفاوت" };
const asList = (v) => (Array.isArray(v) ? v : v ? [v] : []).map((x) => (typeof x === "string" ? x : x?.action || x?.text || x?.title || JSON.stringify(x)));

const ReportEvaluation = ({ ev }) => (
  <div className="space-y-4 text-sm" data-testid="report-evaluation-structured">
    {ev.overall_status && <span className="inline-block rounded-full bg-slate-100 text-slate-700 px-3 py-1 text-xs font-bold">{STATUS_AR[ev.overall_status] || ev.overall_status}</span>}
    {ev.summary && <p className="text-slate-600 leading-loose whitespace-pre-line">{ev.summary}</p>}
    {[["ما نجح", ev.wins], ["الفجوات", ev.gaps], ["الخطة التصحيحية", ev.corrective_plan]].map(([t, v]) => asList(v).length ? (
      <div key={t}>
        <div className="font-bold text-slate-800 mb-1">{t}</div>
        <ul className="list-disc pr-5 space-y-1 text-slate-600">{asList(v).map((x, i) => <li key={i}>{x}</li>)}</ul>
      </div>
    ) : null)}
    {ev.next_targets_hint && <p className="text-slate-500 text-xs">{typeof ev.next_targets_hint === "string" ? ev.next_targets_hint : JSON.stringify(ev.next_targets_hint)}</p>}
  </div>
);


const Section = ({ title, children, testid }) => (
  <section className="mb-8" data-testid={testid}>
    <h2 className="text-xl font-extrabold text-slate-900 mb-3 pb-2 border-b-2 border-slate-900/10">{title}</h2>
    {children}
  </section>
);

const Stat = ({ label, pct, color }) => (
  <div className="rounded-xl border border-slate-200 p-4 bg-white">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-semibold text-slate-600">{label}</span>
      <span className="text-lg font-extrabold text-slate-900">{pct}%</span>
    </div>
    <Bar pct={pct} color={color} />
  </div>
);

export default function ReportView() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/projects/${projectId}/report`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [projectId]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center gap-4" dir="rtl">
        <p className="text-slate-700">تعذّر تحميل التقرير.</p>
        <button onClick={() => navigate(-1)} className="text-primary underline" data-testid="report-back-error">رجوع</button>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center" dir="rtl">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const p = data.project || {};
  const s = data.strategy || null;
  const g = data.goals || null;
  const prog = data.progress || {};
  const cs = data.campaigns_summary || {};
  const vi = (s && s.visual_identity) || {};
  const today = new Date(data.generated_at || Date.now()).toLocaleDateString("ar-SA-u-ca-gregory-nu-latn");

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-3" dir="rtl">
      <style>{PRINT_CSS}</style>

      {/* Toolbar */}
      <div className="no-print max-w-3xl mx-auto mb-4 flex items-center justify-between">
        <button onClick={() => navigate("/app/reports")} className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900" data-testid="report-back-btn">
          <ArrowRight className="w-4 h-4" /> رجوع للتقارير
        </button>
        <button onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-5 py-2.5 font-bold hover:opacity-90" data-testid="report-print-btn">
          <Printer className="w-4 h-4" /> تنزيل / طباعة PDF
        </button>
      </div>

      {/* Document */}
      <div className="report-page max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12 text-slate-800" data-testid="report-document">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-8 pb-6 border-b border-slate-200">
          <div className="flex items-center gap-4">
            {p.logo ? <img src={p.logo} alt="logo" className="w-16 h-16 rounded-xl object-contain border border-slate-200" />
              : <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 font-bold">{(p.name || "?").slice(0, 2)}</div>}
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900" data-testid="report-project-name">{p.name}</h1>
              <p className="text-sm text-slate-500">{p.business_type}</p>
            </div>
          </div>
          <div className="text-end text-xs text-slate-400">
            <div className="font-bold text-primary text-base">رواج الذكية</div>
            <div>تقرير {p.standalone ? "محتوى" : "استراتيجية تنفيذي"}</div>
            <div dir="ltr" className="text-[11px] text-slate-500 mt-1">rawajsmart.com · support@rawajsmart.com · 0557207115</div>
            <div>{today}</div>
          </div>
        </div>

        {/* Intro */}
        <p className="text-slate-600 leading-relaxed mb-8 bg-slate-50 rounded-xl p-4 border border-slate-100">
          يوضّح هذا التقرير الاستراتيجية التسويقية الكاملة لعلامتك وأهدافها وأهم المخرجات المزمع تنفيذها ومستوى التقدّم، ليكون مرجعاً تثقيفياً يطمئنك إلى مسار علامتك ويتيح لك متابعة الإنجاز.
        </p>

        {/* Progress overview */}
        {!p.standalone && (
          <Section title="مستوى التقدّم في التنفيذ" testid="report-progress">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
              <Stat label="التقدّم العام" pct={prog.overall_pct || 0} color="#734F96" />
              <Stat label="نشر المحتوى" pct={prog.upload_pct || 0} color="#D89134" />
              <Stat label="تحقيق المؤشرات" pct={prog.kpi_avg_pct || 0} color="#1C4B3E" />
            </div>
            <div className="space-y-2">
              {(prog.stages || []).map((st) => (
                <div key={st.key} className="flex items-center gap-2 text-sm" data-testid={`report-stage-${st.key}`}>
                  {st.done ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> : <Circle className="w-5 h-5 text-slate-300 shrink-0" />}
                  <span className={st.done ? "text-slate-800 font-semibold" : "text-slate-400"}>{st.label}</span>
                  {st.weight && <span className="ms-auto text-[11px] text-slate-400" data-testid={`report-stage-weight-${st.key}`}>{Math.round(st.weight)}%</span>}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Executive deliverables */}
        <Section title="أهم المخرجات التنفيذية" testid="report-deliverables">
          <div className="space-y-2">
            {(data.deliverables || []).map((d, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                {d.done ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> : <Circle className="w-5 h-5 text-slate-300 shrink-0" />}
                <span className={d.done ? "text-slate-800" : "text-slate-500"}>{d.label}</span>
              </div>
            ))}
          </div>
        </Section>

        {s && s.executive_summary && (
          <Section title="الملخّص التنفيذي" testid="report-summary">
            <p className="text-slate-600 leading-loose whitespace-pre-line">{s.executive_summary}</p>
          </Section>
        )}

        {s && s.swot && (
          <Section title="تحليل SWOT" testid="report-swot">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[["strengths", "نقاط القوة", "#1C4B3E", "bg-emerald-50"],
                ["weaknesses", "نقاط الضعف", "#B45309", "bg-amber-50"],
                ["opportunities", "الفرص", "#1D4ED8", "bg-blue-50"],
                ["threats", "التهديدات", "#B91C1C", "bg-rose-50"]].map(([key, label, color, bg]) => (
                <div key={key} className={`rounded-xl p-4 ${bg}`}>
                  <div className="font-bold mb-2" style={{ color }}>{label}</div>
                  <ul className="list-disc ps-4 space-y-1 text-sm text-slate-700">
                    {(s.swot[key] || []).map((x, i) => <li key={i}>{x}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </Section>
        )}

        {s && s.market_analysis && (
          <Section title="تحليل السوق" testid="report-market">
            <p className="text-slate-600 leading-loose">{s.market_analysis}</p>
          </Section>
        )}

        {s && Array.isArray(s.target_personas) && s.target_personas.length > 0 && (
          <Section title="الشرائح المستهدفة" testid="report-personas">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {s.target_personas.map((tp, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-4">
                  <div className="font-bold text-slate-900 mb-1">{tp.name}</div>
                  <p className="text-sm text-slate-600">{tp.description}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {s && (vi.colors || vi.positioning || vi.logo_concept) && (
          <Section title="الهوية البصرية" testid="report-identity">
            {Array.isArray(vi.colors) && vi.colors.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-4">
                {vi.colors.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="w-8 h-8 rounded-lg border border-slate-200" style={{ background: c.hex }} />
                    <span className="text-slate-600">{c.name} <span className="text-slate-400">{c.hex}</span></span>
                  </div>
                ))}
              </div>
            )}
            {vi.positioning && <p className="text-slate-600 mb-2"><b className="text-slate-800">التموضع: </b>{vi.positioning}</p>}
            {vi.logo_concept && <p className="text-slate-600"><b className="text-slate-800">مفهوم الشعار: </b>{vi.logo_concept}</p>}
            {s.tagline && <p className="text-slate-600 mt-2"><b className="text-slate-800">الشعار النصي: </b>{s.tagline}</p>}
          </Section>
        )}

        {s && Array.isArray(s.content_pillars) && s.content_pillars.length > 0 && (
          <Section title="محاور المحتوى" testid="report-pillars">
            <div className="flex flex-wrap gap-2">
              {s.content_pillars.map((cp, i) => (
                <span key={i} className="rounded-full bg-primary/10 text-primary px-3 py-1 text-sm font-semibold">{cp}</span>
              ))}
            </div>
          </Section>
        )}

        {s && Array.isArray(s.marketing_channels) && s.marketing_channels.length > 0 && (
          <Section title="قنوات التسويق" testid="report-channels">
            <div className="flex flex-wrap gap-2">
              {s.marketing_channels.map((ch, i) => (
                <span key={i} className="rounded-lg border border-slate-200 px-3 py-1 text-sm text-slate-700">{ch}</span>
              ))}
            </div>
          </Section>
        )}

        {g && (
          <Section title="الأهداف ومؤشرات الأداء" testid="report-goals">
            {g.objective && <p className="text-slate-600 mb-4"><b className="text-slate-800">الهدف الرئيسي: </b>{g.objective}</p>}
            <div className="space-y-4">
              {(data.achievements || []).map((k, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="font-semibold text-slate-700">{k.label || k.metric}</span>
                    <span className="text-slate-500">{k.actual} / {k.target} ({k.pct}%)</span>
                  </div>
                  <Bar pct={k.pct} color="#734F96" />
                </div>
              ))}
              {(data.achievements || []).length === 0 && (g.kpis || []).map((k, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-700">{k.label || k.metric}</span>
                  <span className="text-slate-500">المستهدف: {k.target}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {!p.standalone && (
          <Section title="ملخّص خطة النشر" testid="report-campaigns">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div className="rounded-xl border border-slate-200 p-3"><div className="text-2xl font-extrabold text-slate-900">{cs.total_campaigns || 0}</div><div className="text-xs text-slate-500">حملة</div></div>
              <div className="rounded-xl border border-slate-200 p-3"><div className="text-2xl font-extrabold text-slate-900">{cs.total_posts || 0}</div><div className="text-xs text-slate-500">منشور مخطط</div></div>
              <div className="rounded-xl border border-slate-200 p-3"><div className="text-2xl font-extrabold text-slate-900">{cs.with_content || 0}</div><div className="text-xs text-slate-500">محتوى منتَج</div></div>
              <div className="rounded-xl border border-slate-200 p-3"><div className="text-2xl font-extrabold text-slate-900">{cs.uploaded || 0}</div><div className="text-xs text-slate-500">تم نشره</div></div>
            </div>
          </Section>
        )}

        {data.report_text && (
          <Section title="تقييم الأداء والتوصيات" testid="report-evaluation">
            {typeof data.report_text === "string" ? (
              <p className="text-slate-600 leading-loose whitespace-pre-line">{data.report_text}</p>
            ) : (
              <ReportEvaluation ev={data.report_text} />
            )}
          </Section>
        )}

        <div className="mt-10 pt-5 border-t border-slate-200 text-center text-xs text-slate-500 space-y-1" data-testid="report-official-footer">
          <div className="font-bold text-slate-700">مستند صادر من منصة رواج الذكية</div>
          <div dir="ltr">rawajsmart.com · support@rawajsmart.com · 0557207115 · Instagram/TikTok @rawajsmart</div>
          <div className="text-[11px] text-slate-400 leading-relaxed">
            أُعدّ هذا المستند آليًا بالذكاء الاصطناعي بناءً على مدخلات العميل، ويُقدَّم للاسترشاد كما هو دون أدنى مسؤولية على منصة رواج الذكية أو مالكها
            (مكتب براح الرقمية لتقنية المعلومات للاتصالات وتقنية المعلومات — brah-tech.com — الرقم الوطني الموحد 7054088864)؛ القرارات التسويقية والمالية المبنية عليه مسؤولية العميل وحده.
            يمكن مشاركة هذا المستند لأي غرض مع الإشارة إلى مصدره.
          </div>
        </div>
      </div>
    </div>
  );
}
